import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import styles from './Charts.module.css';

const COLORS = ['var(--accent)', 'var(--chart-2, #FFB800)', 'var(--chart-3, #FF3333)'];

interface Props {
  offers: Array<{ month: string; count: number }>;
  favorites: Array<{ month: string; count: number }>;
  messages: Array<{ month: string; count: number }>;
  title?: string;
}

export function EngagementMultiLineChart({ offers, favorites, messages, title }: Props) {
  // Merge all three series by month
  const months = new Set([
    ...offers.map((d) => d.month),
    ...favorites.map((d) => d.month),
    ...messages.map((d) => d.month),
  ]);
  const oMap = new Map(offers.map((d) => [d.month, d.count]));
  const fMap = new Map(favorites.map((d) => [d.month, d.count]));
  const mMap = new Map(messages.map((d) => [d.month, d.count]));

  const data = Array.from(months)
    .sort()
    .map((month) => ({
      label: month.slice(2),
      Offers: oMap.get(month) ?? 0,
      Favorites: fMap.get(month) ?? 0,
      Messages: mMap.get(month) ?? 0,
    }));

  if (data.length === 0) {
    return <div className={styles.empty}>No data</div>;
  }

  return (
    <div className={styles.chartCard}>
      <h3 className={styles.chartTitle}>{title ?? 'Engagement'}</h3>
      <div className={styles.chartWrap}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
            <defs>
              {['Offers', 'Favorites', 'Messages'].map((key, i) => (
                <linearGradient key={key} id={`eng${key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS[i]} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={COLORS[i]} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="label" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} axisLine={{ stroke: 'var(--border)' }} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} axisLine={{ stroke: 'var(--border)' }} tickLine={false} />
            <Tooltip contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: '0.75rem' }} />
            <Legend wrapperStyle={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }} />
            {['Offers', 'Favorites', 'Messages'].map((key, i) => (
              <Area key={key} type="monotone" dataKey={key} stroke={COLORS[i]} fill={`url(#eng${key})`} strokeWidth={2} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
